import { z } from "zod";

/**
 * Promotional campaigns (`MARKETING_PROMOTIONAL_CAMPAIGN`).
 *
 * Three named offers, one rule: every Nth item off by a percentage. The kind
 * is a label a merchant recognises — "half price on the second" — and the
 * numbers underneath are what `pos-core` actually prices with, which is why
 * both travel.
 */

export const CAMPAIGN_KINDS = ["half_price_second", "buy_one_free_one", "nth_item"] as const;
export type CampaignKind = (typeof CAMPAIGN_KINDS)[number];

export const CampaignSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: z.enum(CAMPAIGN_KINDS),
  nthItem: z.number().int(),
  /** Basis points, never a float: 5000 is half off. */
  discountBp: z.number().int(),
  isConsecutive: z.boolean(),
  label: z.string().nullable(),
  itemIds: z.array(z.uuid()),
  validFrom: z.string().nullable(),
  validTo: z.string().nullable(),
  isActive: z.boolean(),
  sort: z.number().int(),
});
export type Campaign = z.infer<typeof CampaignSchema>;

/**
 * The numbers are derived from the kind for the two named offers, so a
 * merchant choosing "buy one free one" cannot also set it to 30% off the third
 * — which would be an offer whose name lies about what it does.
 */
export const SaveCampaignSchema = z.object({
  name: z.string().min(1).max(120),
  kind: z.enum(CAMPAIGN_KINDS),
  /** Only read for `nth_item`; the other two say what they are. */
  nthItem: z.number().int().min(2).max(20).default(2),
  discountBp: z.number().int().min(1).max(10_000).default(5000),
  isConsecutive: z.boolean().default(false),
  label: z.string().max(60).nullable().default(null),
  itemIds: z.array(z.uuid()).max(500).default([]),
  validFrom: z.string().nullable().default(null),
  validTo: z.string().nullable().default(null),
  isActive: z.boolean().default(true),
});

/**
 * Spelled out rather than derived with `.partial()`.
 *
 * `.partial()` makes a field optional but **keeps its `.default()`**, so an
 * omitted key arrives as its default rather than as absent — and a PATCH that
 * meant "rename this" would quietly reset everything it did not mention.
 */
export const UpdateCampaignSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  kind: z.enum(CAMPAIGN_KINDS).optional(),
  nthItem: z.number().int().min(2).max(20).optional(),
  discountBp: z.number().int().min(1).max(10_000).optional(),
  isConsecutive: z.boolean().optional(),
  label: z.string().max(60).nullable().optional(),
  itemIds: z.array(z.uuid()).max(500).optional(),
  validFrom: z.string().nullable().optional(),
  validTo: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
});

/** What each named offer means, so the API and the form cannot disagree. */
export function campaignNumbers(
  kind: CampaignKind,
): { nthItem: number; discountBp: number } | null {
  if (kind === "half_price_second") return { nthItem: 2, discountBp: 5000 };
  if (kind === "buy_one_free_one") return { nthItem: 2, discountBp: 10_000 };
  return null;
}
